import { Observable, of } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
  ILoginDataResponse,
  ILoginForm,
  ILoginRepository,
  ILogSessionPayload,
  ISaveTokenDataResponse,
  ISocialLoginDataResponse,
  ISocialLoginForm,
} from './ILogin.repository';
import {
  AbstractAppConfigService,
  APP_CONFIG_SERVICE,
  IHttpBasicResponse,
  JsonApiResponse,
} from '@boxx/core';
import { IBrandigApiProps } from '@boxx/dynamic-branding';
import { delay, dematerialize, materialize } from 'rxjs/operators';

@Injectable()
export class LoginRepository implements ILoginRepository {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  readonly REFRESH_TOKEN_URL: string = '/api/v1/auth/refresh_token';

  constructor(
    private http: HttpClient,
    @Inject(APP_CONFIG_SERVICE) private appSettings: AbstractAppConfigService
  ) { }

  login(
    credentials: ILoginForm | ISocialLoginForm
  ): Observable<IHttpBasicResponse<ILoginDataResponse>> {
    let params = new HttpParams();

    for (const property in credentials) {
      if (credentials.hasOwnProperty(property)) {
        params = params.append(property, credentials[property]);
      }
    }

    const body = params.toString();

    return this.http.post<IHttpBasicResponse<ILoginDataResponse>>(
      `${this.appSettings.baseUrl()}/api/v2/auth/login`,
      body
    );
  }

  socialLogin(
    credentials: ISocialLoginForm
  ): Observable<IHttpBasicResponse<ISocialLoginDataResponse>> {
    let params = new HttpParams();

    for (const property in credentials) {
      if (credentials.hasOwnProperty(property)) {
        params = params.append(property, credentials[property]);
      }
    }

    const body = params.toString();

    return this.http.post<IHttpBasicResponse<ISocialLoginDataResponse>>(
      `${this.appSettings.baseUrl()}/api/v2/auth/add_social`,
      body
    );
  }

  saveFCMToken(
    deviceId: string,
    fcmRegistrationId: string
  ): Observable<IHttpBasicResponse<ISaveTokenDataResponse>> {
    const data = {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      device_id: deviceId,
      device_type: 'fcm',
      // eslint-disable-next-line @typescript-eslint/naming-convention
      registration_id: fcmRegistrationId,
    };

    let params = new HttpParams();

    for (const property in data) {
      if (data.hasOwnProperty(property)) {
        params = params.append(property, data[property]);
      }
    }

    const body = params.toString();
    return this.http.post<any>(`${this.appSettings.baseUrl()}/api/json/v1/notifications/handle_token`, body);
  }

  logSessionStart(payload: ILogSessionPayload) {
    const params = new HttpParams().append('action_type', payload.action_type);
    // .append('action_desciption', payload.action_description || 'NO_DESCRIPTION_SUPPLIED');

    const urlSearchParams = new URLSearchParams();
    urlSearchParams.append('action_type', payload.action_type);

    const body = params.toString();

    return this.http.post<IHttpBasicResponse<any>>(
      `${this.appSettings.baseUrl()}/api/v1/profileUser/check_app_login`,
      body
    );
  }

  getThemeData(brand: string): Observable<JsonApiResponse<IBrandigApiProps>> {
    return this.http.get<JsonApiResponse<IBrandigApiProps>>(
      `${this.appSettings.baseUrl()}/api/json/v1/theme/get/${brand}`
    );
  }

  sendPasswordRecoveryEmail(email: string): Observable<JsonApiResponse<{ action: string }>> {
    const body = new HttpParams().append('email', email);
    return this.http.post<IHttpBasicResponse<any>>(
      `${this.appSettings.baseUrl()}/api/json/v1/auth/recover_password`,
      body
    );
  }

  deleteAccount(): Observable<JsonApiResponse<any>> {
    return this.http.delete<JsonApiResponse<any>>(
      `${this.appSettings.baseUrl()}/api/json/v1/business/delete`,
    );
  }

  sendRecoveryCode(
    email: string): Observable<IHttpBasicResponse<any>> {
    const body = new HttpParams().append('email', email);
    return this.http.post<IHttpBasicResponse<any>>(
      `${this.appSettings.baseUrl()}/api/json/v1/auth/send_reset_password_code`,
      body
    );
  }

  sendDeleteCode(
    email: string): Observable<IHttpBasicResponse<any>> {
    const body = new HttpParams().append('email', email);
    return this.http.post<IHttpBasicResponse<any>>(
      `${this.appSettings.baseUrl()}/api/json/v2/user/send_delete_user_code`,
      body
    );
  }

  deleteUser(
    code: string): Observable<IHttpBasicResponse<any>> {
    const body = new HttpParams().append('verification_code', code);
    return this.http.post<IHttpBasicResponse<any>>(
      `${this.appSettings.baseUrl()}/api/json/v2/user/delete`,
      body
    );
  }

  validateResetCode(
    data: any): Observable<IHttpBasicResponse<any>> {

    let params = new HttpParams();
    for (const property in data) {
      if (data.hasOwnProperty(property)) {
        params = params.append(property, data[property]);
      }
    }
    const body = params.toString();
    return this.http.post<IHttpBasicResponse<any>>(
      `${this.appSettings.baseUrl()}/api/json/v1/auth/validate_reset_password_code`,
      body
    );
  }

  resetPassword(
    data: any): Observable<IHttpBasicResponse<any>> {

    let params = new HttpParams();
    for (const property in data) {
      if (data.hasOwnProperty(property)) {
        params = params.append(property, data[property]);
      }
    }
    const body = params.toString();
    return this.http.post<IHttpBasicResponse<any>>(
      `${this.appSettings.baseUrl()}/api/json/v1/auth/reset_password`,
      body
    );
  }

  private getBaseUrl() {
    return `${this.appSettings.baseUrl()}/api/${this.appSettings.instance()}/v1`;
  }
}
